import React, { useState } from 'react';
import { prop, isEmpty, compose, equals, sortBy, reverse, isNil, includes } from 'ramda';
import { useQuery } from '@apollo/react-hooks';
import {
  AppProvider,
  Frame,
  Page,
  Card,
  ResourceList,
  EmptyState,
  Filters,
  Stack,
  Badge,
  Tooltip,
  TextStyle,
  Button,
  ChoiceList,
} from '@shopify/polaris';
import moment from 'moment';
import { TitleBar } from '@shopify/app-bridge-react';
import enTranslations from '@shopify/polaris/locales/en.json';
import { GET_QUESTIONNAIRES, GET_CURRENT_SUBSCRIPTION } from '../apollo/queries';
import { noJobsFoundImgUrl } from '../../utils/noJobsFoundImgUrl';
import Footer from './Footer';

const listResourceName = { singular: 'questionnaire', plural: 'questionnaires' };

const sortListOptions = [
  { label: 'Newest', value: 'DATE_CREATED_DESC' },
  { label: 'Oldest', value: 'DATE_CREATED_ASC' },
  { label: 'Name A - Z', value: 'NAME_ASC' },
  { label: 'Name Z - A', value: 'NAME_DESC' },
];

const isNilOrEmpty = (value) => isNil(value) || isEmpty(value);

const Questionnaires = (props) => {
  const { shopifyToken, history = {} } = { ...props };
  const { push: changeRoute = () => {} } = { ...history };
  const [sortValue, setSortValue] = useState('DATE_CREATED_DESC');
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [filterQueryQuestionnaires, setFilterQueryQuestionnaires] = useState();
  const [queryValue, setQueryValue] = useState(null);

  const { loading, data } = useQuery(GET_QUESTIONNAIRES, {
    variables: { shopifyToken },
  });

  const { data: subscriptionData } = useQuery(GET_CURRENT_SUBSCRIPTION, { variables: { shopifyToken } });

  const { questionnaires = [] } = { ...data };
  const { shop = {} } = { ...subscriptionData };
  const { currentSubscription = {} } = { ...shop };
  const isSubscribedUser = !isNilOrEmpty(currentSubscription);

  const isAllowedUser =
    isSubscribedUser &&
    (currentSubscription.subscriptionType === 'Premium' ||
      currentSubscription.subscriptionType === 'Growth' ||
      currentSubscription.subscriptionType === 'Business');

  const emptyStateMarkup = isAllowedUser ? (
    <EmptyState heading='No questionnaires found' image={noJobsFoundImgUrl}>
      {questionnaires.length === 0 ? (
        <Stack vertical>
          <p>Get started creating your first questionnaire</p>
          <Button primary onClick={() => changeRoute(`/questionnaire`)}>
            Create questionnaire
          </Button>
        </Stack>
      ) : (
        <p>Try changing the filters or search term</p>
      )}
    </EmptyState>
  ) : (
    <EmptyState heading='Upgrade your account plan'>
      <Stack vertical>
        <p>Questionnaires are a paid feature for Growth, Business and Premium account plans</p>
        <Button primary onClick={() => changeRoute('/account-plan')}>
          Upgrade account plan
        </Button>
      </Stack>
    </EmptyState>
  );

  const handleFiltersQueryChange = (value) => {
    setQueryValue(value);

    if (isNilOrEmpty(value) && isNilOrEmpty(selectedStatus)) {
      return setFilterQueryQuestionnaires();
    }

    setFilterQueryQuestionnaires(
      questionnaires.filter((item) => {
        const { status, name } = { ...item };
        if (isNilOrEmpty(selectedStatus)) {
          return includes(`${value}`.toLowerCase(), `${name}`.toLowerCase());
        } else {
          return includes(`${value}`.toLowerCase(), `${name}`.toLowerCase()) && includes(status, selectedStatus || []);
        }
      })
    );
  };

  const handleStatusFilter = (value) => {
    setSelectedStatus(value);

    if (isNilOrEmpty(queryValue) && isNilOrEmpty(value)) {
      return setFilterQueryQuestionnaires();
    }

    setFilterQueryQuestionnaires(
      questionnaires.filter((item) => {
        const { status, name } = { ...item };
        if (!!queryValue) {
          return includes(`${queryValue}`.toLowerCase(), `${name}`.toLowerCase()) && includes(status, value);
        } else {
          return includes(status, value);
        }
      })
    );
  };

  const handleQueryValueRemove = () => {
    setQueryValue();
    setFilterQueryQuestionnaires();
  };

  const onSortChangeList = (selected) => {
    setSortValue(selected);

    if (equals(selected, 'DATE_CREATED_DESC')) {
      compose(setFilterQueryQuestionnaires, reverse, sortBy(prop('createdAt')))(rows);
    }

    if (equals(selected, 'DATE_CREATED_ASC')) {
      compose(setFilterQueryQuestionnaires, sortBy(prop('createdAt')))(rows);
    }

    if (equals(selected, 'NAME_ASC')) {
      compose(setFilterQueryQuestionnaires, sortBy(prop('name')))(rows);
    }

    if (equals(selected, 'NAME_DESC')) {
      compose(setFilterQueryQuestionnaires, reverse, sortBy(prop('name')))(rows);
    }
  };

  const rows = filterQueryQuestionnaires || questionnaires;

  const onRenderListItems = (item) => {
    const { id, name, status, totalJobs, updatedAt, jobs = [] } = {
      ...item,
    };
    const jobList = jobs.map(({ title }) => title).join(', ');
    return (
      <ResourceList.Item id={id} accessibilityLabel={`View details for ${name}`}>
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          <div style={{ width: '30%' }}>
            <TextStyle variation='strong'>{name}</TextStyle>
            <br />
            <span style={{ fontWeight: '300', fontSize: '12px' }}>Updated {moment(updatedAt).format('MMMM D, YYYY')}</span>
          </div>

          <p style={{ width: '25%', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999 }}>
            <Tooltip dismissOnMouseOut content={jobList || 'No associated jobs'} preferredPosition={'above'}>
              <span>{totalJobs} jobs</span>
            </Tooltip>
          </p>

          <div style={{ width: '25%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Badge status={status === 'published' ? 'success' : 'default'}>
              <span className={'capitalize'}>{status}</span>
            </Badge>
          </div>
          <div style={{ width: '20%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Button size='slim' onClick={() => changeRoute(`/questionnaire/${id}`)}>
              Edit
            </Button>
          </div>
        </div>
      </ResourceList.Item>
    );
  };

  const disambiguateLabel = (key, value) => {
    switch (key) {
      case 'status':
        const mappedValue = value
          .map((el) => {
            return `${el.charAt(0).toUpperCase()}${el.slice(1)}`;
          })
          .join(', ');
        return `By status: ${mappedValue}`;
      default:
        return value;
    }
  };

  const appliedFilters = !isNilOrEmpty(selectedStatus)
    ? [
        {
          key: 'status',
          label: disambiguateLabel('status', selectedStatus),
          onRemove: () => handleStatusFilter([]),
        },
      ]
    : [];

  return (
    <AppProvider i18n={enTranslations}>
      <Frame>
        <TitleBar title='Questionnaires' breadcrumbs={[{ content: 'Settings', onAction: () => changeRoute('/settings') }]} />
        <Page
          title='Questionnaires'
        breadcrumbs={[{ content: 'Settings', onAction: () => changeRoute('/settings') }]}
          primaryAction={
            isAllowedUser
              ? {
                  content: 'Create questionnaire',
                  onAction: () => changeRoute(`/questionnaire`),
                }
              : undefined
          }>
          <Card>
            <Card.Section>
              {isAllowedUser ? (
                <ResourceList
                  resourceName={listResourceName}
                  filterControl={
                    <Filters
                      filters={[
                        {
                          key: 'status',
                          label: 'Status',
                          filter: (
                            <ChoiceList
                              title='Status'
                              titleHidden
                              choices={[
                                { label: 'Published', value: 'published' },
                                { label: 'Archived', value: 'archived' },
                              ]}
                              selected={selectedStatus || []}
                              onChange={handleStatusFilter}
                              allowMultiple
                            />
                          ),
                          shortcut: true,
                        },
                      ]}
                      appliedFilters={appliedFilters}
                      queryValue={queryValue}
                      onQueryChange={handleFiltersQueryChange}
                      onQueryClear={handleQueryValueRemove}
                    />
                  }
                  items={rows}
                  emptyState={emptyStateMarkup}
                  sortValue={sortValue}
                  hasMoreItems={true}
                  showHeader={true}
                  sortOptions={sortListOptions}
                  onSortChange={onSortChangeList}
                  renderItem={onRenderListItems}
                  totalItemsCount={questionnaires.length}
                  loading={loading}
                />
              ) : (
                <EmptyState heading='Upgrade your account plan'>
                  <Stack vertical>
                    <p>Questionnaires are a paid feature for Growth, Business and Premium account plans</p>
                    <Button primary onClick={() => changeRoute('/account-plan')}>
                      Upgrade account plan
                    </Button>
                  </Stack>
                </EmptyState>
              )}
            </Card.Section>
          </Card>
          <Footer link='https://help.shophire.co/article/48-questionnaires' name='Questionnaires' />
        </Page>
      </Frame>
    </AppProvider>
  );
};
export default Questionnaires;
